import { useState, useEffect, useCallback } from "react";
import { User } from "oidc-client-ts";
import { getUserManager } from "./oidcConfig";

/**
 * React hook for Keycloak OIDC authentication state.
 *
 * Handles:
 * - Callback processing when returning from Keycloak login
 * - Session restoration from localStorage
 * - Silent login via prompt=none (SSO session check)
 * - Token expiry with silent renew fallback
 *
 * Returns isAuthenticated, the current OIDC user, and loading state.
 */
export function useOidcAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const syncUser = useCallback((u: User | null) => {
    if (u && !u.expired) {
      setUser(u);
      setIsAuthenticated(true);
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    const mgr = getUserManager();

    // Handle OIDC callback (authorization code exchange)
    if (window.location.pathname === "/auth/callback") {
      mgr
        .signinRedirectCallback()
        .then((u) => {
          syncUser(u);
          window.history.replaceState({}, document.title, "/");
        })
        .catch((err) => {
          console.error("OIDC callback error:", err);
        })
        .finally(() => setIsLoading(false));
      return;
    }

    // Check existing session, then attempt silent login if no cached user.
    // signinSilent sends prompt=none to Keycloak — if a valid SSO session
    // exists, the user is authenticated without any visible redirect.
    mgr
      .getUser()
      .then((u) => {
        if (u && !u.expired) {
          syncUser(u);
          setIsLoading(false);
        } else {
          return mgr
            .signinSilent()
            .then(syncUser)
            .catch(() => {
              // No active Keycloak session — fall back to login button
              syncUser(null);
            })
            .finally(() => setIsLoading(false));
        }
      })
      .catch(() => setIsLoading(false));

    // Listen for token lifecycle events
    const onLoaded = (u: User) => syncUser(u);
    const onUnloaded = () => {
      setUser(null);
      setIsAuthenticated(false);
    };

    mgr.events.addUserLoaded(onLoaded);
    mgr.events.addUserUnloaded(onUnloaded);
    mgr.events.addAccessTokenExpired(() => {
      mgr.signinSilent().catch(() => {
        setUser(null);
        setIsAuthenticated(false);
      });
    });

    return () => {
      mgr.events.removeUserLoaded(onLoaded);
      mgr.events.removeUserUnloaded(onUnloaded);
    };
  }, [syncUser]);

  return { isAuthenticated, user, isLoading };
}
